// Tipos e helpers do controle de budget (obra → máquinas → gastos por etapa)

export interface BudgetGasto {
  id: string;
  etapa?: string | null;
  tipo: 'realizado' | 'previsto' | string;
  valor: number;
  data: string;
}

export interface BudgetMaquina {
  id: string;
  nome: string;
  rubricaId?: string | null;
  budget: number;
  gastos: BudgetGasto[];
  gastoRealizado: number;
  gastoPrevisto: number;
  saldo: number;
}

/** Frente de custo da obra (M.O instalação, projeto mecânico...) = grupo de colunas */
export interface BudgetRubrica {
  id: string;
  nome: string;
  ordem: number;
  /** false = rubrica DESVINCULADA: tem linhas próprias, fora do pivô das máquinas */
  vinculada?: boolean;
  budgetTotal: number;
  gastoRealizado: number;
  gastoPrevisto: number;
}

export interface BudgetObraTotais {
  budgetTotal: number;
  gastoRealizado: number;
  gastoPrevisto: number;
  saldo: number;
  foraDoBudget: number;
}

export interface BudgetObra {
  id: string;
  nome: string;
  descricao?: string | null;
  maquinas: BudgetMaquina[];
  rubricas: BudgetRubrica[];
  totais: BudgetObraTotais;
}

export interface BudgetResumo {
  projetoId: string;
  projetoNome: string;
  budgetHabilitado: boolean;
  obras: BudgetObra[];
  totais: { budgetTotal: number; gastoRealizado: number; gastoPrevisto: number; foraDoBudget: number; saldo: number };
}

export interface EtapaCol {
  key: string;
  nome: string | null;
  tipo: string;
}

export const SEM_ETAPA = '__sem_etapa__';

// Colunas de lançamento padrão (sempre visíveis, como na planilha)
export const ETAPAS_PADRAO: EtapaCol[] = [
  { key: '1ª etapa (conceitual)', nome: '1ª etapa (conceitual)', tipo: 'realizado' },
  { key: '2ª etapa (detalhado)', nome: '2ª etapa (detalhado)', tipo: 'realizado' },
];

export const moeda = (valor: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);

// Aceita "2.576,25", "2576,25", "2576.25", "R$ 1.000"
export function parseValor(texto: string): number | null {
  const limpo = texto.replace(/R\$/gi, '').replace(/\s/g, '');
  if (limpo === '') return 0;
  const normalizado = limpo.includes(',') ? limpo.replace(/\./g, '').replace(',', '.') : limpo;
  const n = Number(normalizado);
  return isNaN(n) ? null : n;
}

// ===== Pivô por rubrica (uma linha por máquina, um grupo de colunas por rubrica) =====

export const SEM_RUBRICA = '__sem_rubrica__';

/** Um grupo de colunas da planilha: a rubrica e as etapas lançadas dentro dela */
export interface GrupoRubrica {
  id: string;
  nome: string;
  etapas: EtapaCol[];
  /** false = desvinculada: as linhas dela ficam num bloco separado, no final */
  vinculada: boolean;
}

/**
 * Uma linha da planilha. Nas rubricas vinculadas é a máquina vista em todas elas;
 * com `grupoExclusivo` é uma linha própria de uma rubrica desvinculada (ex.:
 * "Hospedagem" dentro de "despesa"), que não divide a linha com máquina nenhuma.
 */
export interface LinhaPivot {
  nome: string;
  porRubrica: Record<string, BudgetMaquina | undefined>;
  grupoExclusivo?: string;
  budgetTotal: number;
  gastoRealizado: number;
  gastoPrevisto: number;
  saldo: number;
}

/** Linha ainda sem nenhuma linha de budget no banco (o slot vazio de um bloco) */
export const linhaVazia = (linha?: LinhaPivot) =>
  !linha || Object.values(linha.porRubrica).filter(Boolean).length === 0;

/** Etapas lançadas dentro de uma rubrica (mínimo 1 coluna, para haver onde digitar) */
function etapasDoGrupo(linhas: BudgetMaquina[]): EtapaCol[] {
  const mapa = new Map<string, EtapaCol>();
  linhas.forEach((m) => m.gastos.forEach((g) => {
    const key = g.etapa ?? SEM_ETAPA;
    if (!mapa.has(key)) mapa.set(key, { key, nome: g.etapa ?? null, tipo: g.tipo });
  }));
  const etapas = [...mapa.values()];
  return etapas.length > 0 ? etapas : [ETAPAS_PADRAO[0]];
}

/**
 * Grupos de colunas da obra. Sem rubrica cadastrada a obra segue como antes
 * (um grupo único com as etapas soltas), então planilhas antigas não mudam.
 */
export function gruposDaObra(obra: BudgetObra): GrupoRubrica[] {
  const rubricas = [...(obra.rubricas ?? [])].sort((a, b) => a.ordem - b.ordem);
  const semRubrica = obra.maquinas.filter((m) => !m.rubricaId);

  const grupos: GrupoRubrica[] = rubricas.map((r) => ({
    id: r.id,
    nome: r.nome,
    etapas: etapasDoGrupo(obra.maquinas.filter((m) => m.rubricaId === r.id)),
    vinculada: r.vinculada !== false,
  }));

  if (semRubrica.length > 0 || grupos.length === 0) {
    grupos.push({
      id: SEM_RUBRICA,
      nome: grupos.length === 0 ? 'Budget' : 'Sem rubrica',
      etapas: grupos.length === 0 ? etapasDaObra(obra) : etapasDoGrupo(semRubrica),
      vinculada: true,
    });
  }
  return grupos;
}

/**
 * Ordem das colunas na planilha: as rubricas desvinculadas vão para a direita,
 * junto do bloco de linhas delas no final — assim cada bloco fica num canto só.
 */
export function ordenarGrupos(grupos: GrupoRubrica[]): GrupoRubrica[] {
  return [...grupos.filter((g) => g.vinculada), ...grupos.filter((g) => !g.vinculada)];
}

function linhaNova(nome: string, grupoExclusivo?: string): LinhaPivot {
  return {
    nome,
    porRubrica: {},
    ...(grupoExclusivo ? { grupoExclusivo } : {}),
    budgetTotal: 0,
    gastoRealizado: 0,
    gastoPrevisto: 0,
    saldo: 0,
  };
}

function acumular(linha: LinhaPivot, m: BudgetMaquina) {
  linha.budgetTotal += m.budget;
  linha.gastoRealizado += m.gastoRealizado;
  linha.gastoPrevisto += m.gastoPrevisto;
  linha.saldo = linha.budgetTotal - linha.gastoRealizado;
}

/**
 * As linhas da planilha, na ordem em que aparecem.
 *
 * Rubricas VINCULADAS: uma linha por máquina, juntando as linhas de mesmo nome
 * que estavam em abas diferentes. Com `rubricasAtivas` o consolidado soma só
 * essas rubricas (bate com os cards do topo), mas a máquina continua na planilha
 * mesmo sem linha na rubrica ativa — é assim que se digita numa rubrica vazia.
 * Com SÓ rubrica desvinculada na tela as máquinas somem: não haveria uma coluna
 * sequer para preencher nelas, só linha em branco atrapalhando.
 *
 * Rubricas DESVINCULADAS: cada linha é só dela (Hospedagem, Frete, Refeição não
 * são máquinas), então vão num bloco no final, na ordem das rubricas, e só
 * enquanto a rubrica está ativa. Um bloco ainda sem item nenhum entra com uma
 * linha em branco, senão não haveria onde digitar o primeiro (nos que já têm
 * item, quem faz esse papel é a linha em branco do fim da planilha).
 */
export function linhasDaObra(obra: BudgetObra, rubricasAtivas?: string[]): LinhaPivot[] {
  const ativas = rubricasAtivas ? new Set(rubricasAtivas) : null;
  const desvinculadas = new Map<string, number>();
  (obra.rubricas ?? []).forEach((r) => {
    if (r.vinculada === false) desvinculadas.set(r.id, r.ordem);
  });

  const maquinas = new Map<string, LinhaPivot>();
  const blocos = new Map<string, LinhaPivot[]>();
  desvinculadas.forEach((_ordem, id) => {
    if (!ativas || ativas.has(id)) blocos.set(id, []);
  });
  // As linhas de máquina só existem para as rubricas vinculadas
  const mostrarMaquinas = !ativas || [...ativas].some((id) => !desvinculadas.has(id));

  for (const m of obra.maquinas) {
    const rubrica = m.rubricaId ?? SEM_RUBRICA;

    const bloco = blocos.get(rubrica);
    if (desvinculadas.has(rubrica)) {
      // Rubrica desvinculada tirada do budget: as linhas dela saem da planilha
      // junto, em vez de virarem linhas em branco no meio das máquinas.
      if (!bloco) continue;
      const linha = linhaNova(m.nome, rubrica);
      linha.porRubrica[rubrica] = m;
      acumular(linha, m);
      bloco.push(linha);
      continue;
    }

    if (!mostrarMaquinas) continue;

    const chave = m.nome.trim();
    let linha = maquinas.get(chave);
    if (!linha) {
      linha = linhaNova(m.nome);
      maquinas.set(chave, linha);
    }
    // Duas linhas na mesma rubrica só acontecem em dado legado; a segunda é ignorada
    // no pivô mas continua no banco, então nada some sem o usuário mandar.
    if (!linha.porRubrica[rubrica]) linha.porRubrica[rubrica] = m;
    if (ativas && !ativas.has(rubrica)) continue;
    acumular(linha, m);
  }

  const ordemDosBlocos = [...blocos.keys()].sort(
    (a, b) => (desvinculadas.get(a) ?? 0) - (desvinculadas.get(b) ?? 0)
  );
  return [
    ...maquinas.values(),
    ...ordemDosBlocos.flatMap((id) => {
      const bloco = blocos.get(id)!;
      return bloco.length > 0 ? bloco : [linhaNova('', id)];
    }),
  ];
}

// ===== Totais por rubrica (o topo da tela mostra só as rubricas ativas) =====

export interface TotaisGrupo {
  budgetTotal: number;
  gastoRealizado: number;
  gastoPrevisto: number;
  saldo: number;
  foraDoBudget: number;
}

const TOTAIS_ZERO: TotaisGrupo = {
  budgetTotal: 0,
  gastoRealizado: 0,
  gastoPrevisto: 0,
  saldo: 0,
  foraDoBudget: 0,
};

/**
 * Totais de cada grupo de colunas, somados das linhas de budget. Vem daqui e não
 * de `obra.rubricas` porque o grupo "Sem rubrica" não tem registro de rubrica.
 */
export function totaisPorGrupo(obra: BudgetObra): Record<string, TotaisGrupo> {
  const mapa: Record<string, TotaisGrupo> = {};
  for (const m of obra.maquinas) {
    const id = m.rubricaId ?? SEM_RUBRICA;
    const t = mapa[id] ?? { ...TOTAIS_ZERO };
    t.budgetTotal += m.budget;
    t.gastoRealizado += m.gastoRealizado;
    t.gastoPrevisto += m.gastoPrevisto;
    t.foraDoBudget += Math.max(0, m.gastoRealizado - m.budget);
    t.saldo = t.budgetTotal - t.gastoRealizado;
    mapa[id] = t;
  }
  return mapa;
}

/** Soma os totais das rubricas ativas — o budget que aparece nos cards */
export function somarGrupos(totais: Record<string, TotaisGrupo>, ids: string[]): TotaisGrupo {
  const soma = { ...TOTAIS_ZERO };
  for (const id of ids) {
    const t = totais[id];
    if (!t) continue;
    soma.budgetTotal += t.budgetTotal;
    soma.gastoRealizado += t.gastoRealizado;
    soma.gastoPrevisto += t.gastoPrevisto;
    soma.foraDoBudget += t.foraDoBudget;
  }
  soma.saldo = soma.budgetTotal - soma.gastoRealizado;
  return soma;
}

export function etapasDaObra(obra: BudgetObra): EtapaCol[] {
  const mapa = new Map<string, EtapaCol>();
  obra.maquinas.forEach((m) => m.gastos.forEach((g) => {
    const key = g.etapa ?? SEM_ETAPA;
    if (!mapa.has(key)) {
      mapa.set(key, { key, nome: g.etapa ?? null, tipo: g.tipo });
    }
  }));
  const etapas = [...mapa.values()];
  // Garante no mínimo 2 colunas de lançamento, como na planilha
  for (const padrao of ETAPAS_PADRAO) {
    if (etapas.length >= 2) break;
    if (!etapas.some((e) => e.key === padrao.key)) etapas.push(padrao);
  }
  return etapas;
}

export function valorEtapa(m: BudgetMaquina, col: EtapaCol): number {
  return m.gastos
    .filter((g) => (g.etapa ?? SEM_ETAPA) === col.key)
    .reduce((s, g) => s + g.valor, 0);
}

// ===== Recalculo local (edição fluida, sem recarregar a tela) =====

export function recalcularMaquina(m: BudgetMaquina): BudgetMaquina {
  const gastoRealizado = m.gastos.filter((g) => g.tipo !== 'previsto').reduce((s, g) => s + g.valor, 0);
  const gastoPrevisto = m.gastos.filter((g) => g.tipo === 'previsto').reduce((s, g) => s + g.valor, 0);
  return { ...m, gastoRealizado, gastoPrevisto, saldo: m.budget - gastoRealizado };
}

export function recalcularObra(o: BudgetObra): BudgetObra {
  const maquinas = o.maquinas.map(recalcularMaquina);
  const budgetTotal = maquinas.reduce((s, m) => s + m.budget, 0);
  const gastoRealizado = maquinas.reduce((s, m) => s + m.gastoRealizado, 0);
  const gastoPrevisto = maquinas.reduce((s, m) => s + m.gastoPrevisto, 0);
  const foraDoBudget = maquinas.reduce((s, m) => s + Math.max(0, m.gastoRealizado - m.budget), 0);
  const rubricas = (o.rubricas ?? []).map((r) => {
    const linhas = maquinas.filter((m) => m.rubricaId === r.id);
    return {
      ...r,
      budgetTotal: linhas.reduce((s, m) => s + m.budget, 0),
      gastoRealizado: linhas.reduce((s, m) => s + m.gastoRealizado, 0),
      gastoPrevisto: linhas.reduce((s, m) => s + m.gastoPrevisto, 0),
    };
  });
  return {
    ...o,
    maquinas,
    rubricas,
    totais: {
      budgetTotal,
      gastoRealizado,
      gastoPrevisto,
      saldo: budgetTotal - gastoRealizado,
      foraDoBudget
    }
  };
}
